// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import axios from "axios";
import { store } from "@/store";
import * as types from "@/utils/Types";

/**
 * Gets the information about the currently logged in user
 * @returns A UserInfo object for the current user
 */
async function getCurrentUserInfo(): Promise<types.userInfo | null> {
  try {
    console.log("getuserinfo");
    const res = await axios.get<types.userInfo>(`/User/Info`);
    console.log("res", res);
    //Update preferences in the database, if not been set yet
    setDefaultUserPreferences(res.data);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Sets the information about the currently logged in user
 * @returns The HTTP response code
 */
async function setCurrentUserInfo(): Promise<number | null> {
  try {
    const res = await axios.get<types.userInfo>(`/User/Info`);
    store.commit("setUserInfo", res.data);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets the information of the specified user
 * @param userId Id of the user from which you require more information
 * @returns A UserInfo object
 */
async function getUserInfo(userId: number): Promise<types.userInfo | null> {
  try {
    const res = await axios.get<types.userInfo>(`/User/Info/${userId}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Checks if the preferences for specific user are already set
 * If not, it updates them in the database to some default values
 * @param user The specified user for whom to set the preferences
 */
function setDefaultUserPreferences(user: types.userInfo) {
  if (typeof user.preferences != "object" || !user.preferences.ideLang) {
    user.preferences = {
      ideLang: "nl",
      siteLang: "nl",
      programmingStyle: "schematically",
      soundEnabled: true,
      musicEnabled: true,
    };
    setUserPreferences(user.preferences);
  }
}

/**
 * Updates the user preferences in the backend
 * @param preferences The updated user preferences
 * @returns The HTTP response code
 */
async function setUserPreferences(
  preferences: types.userPrefs
): Promise<number | null> {
  try {
    const res = await axios.put<types.userPrefs>(
      `/User/Preferences`,
      preferences,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Set the avatar id in the store and updates the new avatar id in the backend.
 * @param avatarId The updated avatar id
 * @returns The HTTP response code
 */
async function setAvatarId(avatarId: number): Promise<number | null> {
  try {
    //Set the avatar id in the store.
    store.commit("setAvatarId", avatarId);
    //Update avatar id in the database
    const res = await axios.put<void>(`/User/Avatar/${avatarId}`);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Get the number of medals the user has earned for all the medal types.
 * @returns The amount of medals of each medal type
 */
async function getMedals(): Promise<types.medals | null> {
  try {
    const res = await axios.get<types.medals>(`/Progress/Medals`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Get the all the achievements of the user.
 * @returns List of the achievements
 */
async function getAchievements(): Promise<types.achievements | null> {
  try {
    const res = await axios.get<types.achievements>(`/User/Achievements`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export {
  getCurrentUserInfo,
  setCurrentUserInfo,
  getUserInfo,
  setUserPreferences,
  setAvatarId,
  getMedals,
  getAchievements,
};
